import Section from '../components/shared/Section'
import Button from '../components/shared/Button'
import Card from '../components/shared/Card'
import SEO from '../components/shared/SEO'
import { ROUTE_META } from '../lib/seo'
import { ArrowRight } from 'lucide-react'

export default function Home() {
  return (
    <>
      <SEO
        title={ROUTE_META['/'].title}
        description={ROUTE_META['/'].description}
        jsonLd={ROUTE_META['/'].jsonLd}
      />

      {/* Hero Section */}
      <Section className="min-h-[calc(100vh-5rem)] flex items-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-neon-lime/5 via-neon-purple/5 to-neon-pink/5 pointer-events-none" />
        <div className="absolute top-20 right-20 w-96 h-96 bg-neon-lime/10 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-neon-pink/10 rounded-full blur-3xl" />
        <div className="text-center max-w-5xl mx-auto relative z-10">
          <h1 className="text-5xl md:text-7xl lg:text-8xl text-neutral-100 mb-8 leading-tight">
            People do not want products or services. They want{' '}
            <span className="bg-gradient-to-r from-neon-lime via-neon-yellow to-neon-orange bg-clip-text text-transparent">
              outcomes
            </span>
            .
          </h1>
          <p className="text-xl md:text-2xl text-neutral-300 mb-6 max-w-3xl mx-auto leading-relaxed">
            I help organisations design the systems, experiences and operations that make those outcomes possible.
          </p>
          <p className="text-lg text-neutral-500 mb-10 max-w-2xl mx-auto">
            For startups, scale-ups and organisations building technology products and services.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Button variant="primary" size="large" onClick={() => window.location.href = '/contact#booking'}>
              Book a discovery call
            </Button>
            <a href="mailto:hello@mathosconsult.com">
              <Button variant="secondary" size="large">Get in touch</Button>
            </a>
          </div>
        </div>
      </Section>

      {/* Education Section — What is service design */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-lime mb-8">
            Your product or service is only part of what your customer experiences.
          </h2>
          <div className="space-y-6">
            <p className="text-xl text-neutral-400 leading-relaxed">
              Think about buying a car. The product is the vehicle — but the experience includes everything around it: researching, test-driving, financing, insuring, servicing, getting help when something goes wrong, upgrading, and eventually selling it on. Every one of those moments shapes how people feel about the company behind it.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              People's needs are not static either. They differ between individuals and change over time — from first discovering a product or service, to learning how to use it, seeking support, adapting to changing circumstances, and deciding whether the relationship still meets their needs.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              Many organisations focus heavily on designing the product itself while leaving the wider experience fragmented across departments, systems, and processes.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              Service design is about understanding and shaping that wider experience — aligning products, services, operations, and human needs so they work together coherently.
            </p>
            <p className="text-xl text-neutral-300">
              That's what I do.
            </p>
          </div>
        </div>
      </Section>

      {/* Why This Matters — Business case */}
      <Section background="gray">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl text-neon-orange mb-10">
            Why this matters for your business.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-10">
            <div className="text-center">
              <p className="text-5xl text-neon-lime mb-3">2x</p>
              <p className="text-neutral-400">
                Companies that lead on design grow revenue at twice the rate of their industry
              </p>
            </div>
            <div className="text-center">
              <p className="text-5xl text-neon-pink mb-3">40%</p>
              <p className="text-neutral-400">
                of companies still aren't talking to their end users during development
              </p>
            </div>
            <div className="text-center">
              <p className="text-5xl text-neon-orange mb-3">&lt;5%</p>
              <p className="text-neutral-400">
                of companies can make objective, evidence-based design decisions
              </p>
            </div>
          </div>
          <p className="text-neutral-500 text-sm text-center">
            Source: McKinsey & Company, The Business Value of Design — 300 companies tracked over 5 years
          </p>
          <div className="mt-8 max-w-3xl mx-auto space-y-4 text-center">
            <p className="text-xl text-neutral-300">
              The most expensive mistake isn't building something badly — it's building from the wrong understanding of reality.
            </p>
            <p className="text-xl text-neutral-300">
              I help organisations understand how people actually experience their systems, products, and services before solutions are designed.
            </p>
          </div>
        </div>
      </Section>

      {/* What You Can Expect — Outcomes */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-purple mb-10">
            What you can expect
          </h2>
          <div className="space-y-6">
            {[
              "A clearer understanding of how people actually experience your products, services, and systems",
              "Better alignment between operational processes and the human outcomes they are meant to support",
              "Earlier visibility into problems, friction, and organisational assumptions before they become expensive to scale",
              "Decisions grounded in real behaviours, lived experience, and operational reality rather than isolated metrics or internal opinion",
              "A systems-level perspective that connects products, services, operations, and organisational strategy rather than treating them separately",
              "An experienced partner focused not only on delivery, but on whether the overall experience genuinely works for the people involved",
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 text-lg text-neutral-400 leading-relaxed">
                <span className="text-neon-purple mt-1 shrink-0">—</span>
                <span>{item}</span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* Differentiators — framed around customer situation */}
      <Section background="gray">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl text-neutral-100 mb-4">
            Sound familiar?
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          <Card>
            <h3 className="text-2xl text-neon-lime mb-4">
              You're about to invest serious money — and you're not sure the brief is right.
            </h3>
            <p className="text-neutral-400 leading-relaxed">
              I work upstream, at the point where decisions get made — before they become constraints. Getting the question right before you build is where the real leverage is. That's where I start.
            </p>
          </Card>

          <Card>
            <h3 className="text-2xl text-neon-pink mb-4">
              You've had consultants before. They left a deck. Nothing changed.
            </h3>
            <p className="text-neutral-400 leading-relaxed">
              I have a 30-year track record of designing and building working systems — platforms, tools, processes, data infrastructure. The output isn't a slide deck. It's something that functions.
            </p>
          </Card>

          <Card>
            <h3 className="text-2xl text-neon-orange mb-4">
              You need someone at your level, not a junior team managed by a partner you rarely see.
            </h3>
            <p className="text-neutral-400 leading-relaxed">
              I work directly with founders and leadership teams. No handoff to junior staff. When you hire Mathos Consult, you get me — 30 years of experience, personally accountable to the outcome.
            </p>
          </Card>
        </div>
      </Section>

      {/* Proof — condensed */}
      <Section>
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl text-neon-orange mb-10">The track record.</h2>
          <div className="space-y-6">
            {[
              { text: "Bluechain — Joined as Lead Service Designer, progressed to COO. Built an innovative payment platform from scratch. 0 to 3,000 customers, £9M+/month in transactions.", highlight: "Lead Service Designer → COO" },
              { text: "NHS / Great Ormond Street Hospital — Designed and built POEM, a first-to-market clinical trial management system. Faster treatments, lower costs.", highlight: "First-to-market NHS platform" },
              { text: "American Express — 15 years, four roles. PSD2 compliance across 56 countries. Consolidated 11 fragmented KYC data providers into one.", highlight: "15 years, global scale" },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-4 text-lg text-neutral-400 leading-relaxed">
                <span className="text-neon-lime mt-1 shrink-0">—</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>
          <div className="mt-8">
            <Button variant="outline" onClick={() => window.location.href = '/about'}>
              See the full story <ArrowRight className="inline ml-2" size={20} />
            </Button>
          </div>
        </div>
      </Section>

      {/* Who I Work With */}
      <Section background="gray">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-pink mb-4">Who I work with.</h2>
          <p className="text-lg text-neutral-400 mb-8">
            I work best with people who are trying to build something genuinely better — not incrementally better, but fundamentally better than the way it was done before.
          </p>
          <ul className="space-y-4 mb-10">
            {[
              "Founders and startups using technology to tackle a real problem — who want to build it right from day one",
              "Scale-ups who know the way they built things initially won't hold as they grow, and want to get ahead of that",
              "Organisations who need a senior partner to help them design and build properly, rather than discover the problems after launch",
              "Leaders who've been burned before — who understand what it costs to build the wrong thing, and aren't willing to repeat it",
            ].map((item, index) => (
              <li key={index} className="flex items-start gap-3 text-lg text-neutral-400">
                <span className="text-neon-lime font-semibold text-2xl">✓</span>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </Section>

      {/* Built with you, not handed to you */}
      <Section>
        <div className="max-w-3xl mx-auto">
          <h2 className="text-4xl text-neon-purple mb-8">Built with you, not handed to you.</h2>
          <div className="space-y-6">
            <p className="text-xl text-neutral-400 leading-relaxed">
              One of the recurring problems in consulting is the separation between strategy and reality. Organisations are often given recommendations, frameworks, or transformation plans without being meaningfully involved in shaping them or supported through the complexity of making them work operationally.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              Real change rarely happens through documents alone.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              The most effective work tends to happen collaboratively — through understanding the organisation, working alongside the people inside it, testing assumptions against reality, and adapting as new understanding emerges.
            </p>
            <p className="text-xl text-neutral-400 leading-relaxed">
              My approach is intentionally hands-on. I work across strategy, operations, products, and services to help organisations not only define better outcomes, but also build the operational conditions that make those outcomes possible.
            </p>
            <p className="text-xl text-neutral-300 leading-relaxed">
              The goal is not simply to produce deliverables. It is to leave the organisation with clearer understanding, better alignment, and systems that work more coherently for the people using and delivering them.
            </p>
          </div>
        </div>
      </Section>

      {/* CTA Section */}
      <Section background="gray">
        <div className="text-center max-w-3xl mx-auto">
          <p className="text-2xl md:text-3xl text-neutral-100 mb-8 leading-relaxed">
            Let's design something your customers actually want — and build it so it works.
          </p>
          <div className="flex flex-col sm:flex-row gap-5 justify-center items-center">
            <Button variant="primary" size="large" onClick={() => window.location.href = '/contact#booking'}>
              Book a discovery call
            </Button>
            <a href="mailto:hello@mathosconsult.com">
              <Button variant="secondary" size="large">Get in touch</Button>
            </a>
          </div>
        </div>
      </Section>
    </>
  )
}
